import React, { useState, useEffect, createRef } from "react"
import withStyles from "react-jss"
import { Loading, Question } from "../components"
import { NO_OF_COLORS } from "../data/colors"
import { RESULT_IDS } from "../data/uniqueValues"
import { data } from "../data/data"
import {
  formatQuestionnaireData,
  getScrollPosition,
  hijackPaths,
} from "../utils/helper"
import {
  getPathFromHeadphoneToStart,
  getPathsFromStartToQ1,
  getPathsBetweenQuestions,
} from "../utils/lines"
import { BREAKPOINT } from "../data/uniqueValues"
import { preloadImages } from "../utils/preloadImages"
import { results } from "../data/results"
import { HeadphoneJack } from "../assets/images"

const styles = {
  container: {
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  questions: {
    display: "flex",
    flexDirection: "column",
    margin: "6vh 8vw 0 8vw",
    width: "100%",
    zIndex: 2,
    maxWidth: "1100px",
    [`@media only screen and (max-width: ${BREAKPOINT}px)`]: {
      margin: "0 6vmin",
    },
  },
  results: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    background: "white",
    color: "black",
    borderBottom: "1px solid black",
    padding: "1rem",
    zIndex: 100000,
  },
}

const Quiz = ({
  classes,
  showResults,
  setShowResults,
  setHeadphoneId,
  quizRef,
  resultRef,
  resetQuiz,
  setResetQuiz,
  scrollToHeader,
  headerCardRef,
  startRef,
  headphoneRef,
  setPaths,
  appWidth,
  appHeight,
  svgRef,
  startQuiz,
}) => {
  useEffect(() => {
    // Preloads the headphone jack image to avoid
    // image not showing up within the 3s in the loading card
    // in slow network devices
    preloadImages([HeadphoneJack])
  }, [])

  const INITIAL_QNO = 1,
    INITIAL_COLOR_INDEX = 0

  const [questionnaire, setQuestionnaire] = useState(() =>
    formatQuestionnaireData([], data[0], INITIAL_QNO, INITIAL_COLOR_INDEX)
  )
  const [possibleResults, setPossibleResults] = useState(RESULT_IDS)

  const [qNo, setQNo] = useState(INITIAL_QNO)
  const [colorIndex, setColorIndex] = useState(INITIAL_COLOR_INDEX)

  const triggerResults = (possibleResults, colorIndex) => {
    // Show loading card
    setQuestionnaire((data) => [
      ...data,
      {
        loading: true,
        qRef: createRef(),
        headingRef: createRef(),
      },
    ])

    // Get headphone id and preload the image for better UX
    const resultId = possibleResults[0]
    if (resultId) {
      const headPhone = results.find((d) => d.resultId === resultId)
      preloadImages([headPhone.imgURL])
    }

    // Show results
    setTimeout(() => {
      setShowResults(true)
      setHeadphoneId(resultId)
    }, 3000)
  }

  const updateChoice = (qId, selectedOptionId) => {
    // If user tries to change answer for old questions, don't allow
    if (questionnaire.find((d) => d.qId === qId).lockAnswer) return

    // Find the selected answer in questionnaire
    const selectedOptionData = questionnaire
      .find((d) => d.qId === qId)
      .options.find((d) => d.optionId === selectedOptionId)

    // Update user choice and lock the answer
    const userChoice = questionnaire.map((d) => {
      if (d.qId === qId) {
        return {
          ...d,
          lockAnswer: true,
          options: d.options.map((ans) => ({
            ...ans,
            selected: selectedOptionId === ans.optionId ? true : false,
          })),
        }
      } else return d
    })

    const nextQId = selectedOptionData.nextQId
    const newQNo = qNo + 1
    setQNo(newQNo)
    const newColorIndex = colorIndex + 1 > NO_OF_COLORS - 1 ? 0 : colorIndex + 1
    setColorIndex(newColorIndex)

    // Fix for paths with issues. Refer data/pathsWithIssues.js
    const debugData = hijackPaths(userChoice)
    console.log(debugData)
    if (debugData.needsHijacking) {
      triggerResults(debugData.resultIds, newColorIndex)
      return
    }

    // Update user's possibleResultIds
    const filteredPossibleResults = possibleResults.filter((id) =>
      selectedOptionData.possibleResultsIds.includes(id)
    )
    setPossibleResults(filteredPossibleResults)

    /**
     * Result is set if -
     * 1. User's possibleResults just has 1 id
     * 2. There's no nextQId in data
     */
    if (filteredPossibleResults.length === 1 || !nextQId) {
      triggerResults(filteredPossibleResults, newColorIndex)
      return
    }
    // If the quiz hasn't ended and there are next questions to ask
    else if (nextQId) {
      const nextQData = data[nextQId]

      // Filters out answer options for the next question
      // that do not align with previous user choices
      const filteredOptions = nextQData.options.filter((option) =>
        filteredPossibleResults.some(
          (id) => option.possibleResultsIds.indexOf(id) >= 0
        )
      )

      // If there is only one option available, make choices
      // for user till there's a question with more than one option
      if (filteredOptions.length === 1) {
        let newFilteredPossibleResults = filteredPossibleResults.filter((id) =>
          filteredOptions[0].possibleResultsIds.includes(id)
        )

        let nextQId1 = filteredOptions[0].nextQId
        if (!nextQId1) {
          // If there is no next Q, and we select the only option available,
          // update user's possible results for that selection and show result
          setPossibleResults(newFilteredPossibleResults)
          triggerResults(newFilteredPossibleResults, newColorIndex)
          return
        }
        let nextQData1 = data[nextQId1]
        let filteredOptions1 = nextQData1.options

        // find next question that has more than one option
        while (filteredOptions1.length === 1) {
          filteredOptions1 = nextQData1.options.filter((option) =>
            newFilteredPossibleResults.some(
              (id) => option.possibleResultsIds.indexOf(id) >= 0
            )
          )
          if (filteredOptions1.length === 1) {
            newFilteredPossibleResults = newFilteredPossibleResults.filter(
              (id) => filteredOptions1[0].possibleResultsIds.includes(id)
            )
            nextQId1 = filteredOptions1[0].nextQId
            if (!nextQId1) {
              // If there is no next Q, and we select the only option available,
              // update user's possible results for that selection and show result
              setPossibleResults(newFilteredPossibleResults)
              triggerResults(newFilteredPossibleResults, newColorIndex)
              return
            }
            nextQData1 = data[nextQId1]
            filteredOptions1 = nextQData1.options
          }
        }
        // Format data
        const dataWithNextQuestion = formatQuestionnaireData(
          userChoice,
          nextQData1,
          newQNo,
          newColorIndex,
          filteredOptions1
        )
        setQuestionnaire(dataWithNextQuestion)
        return
      }

      // Format data
      const dataWithNextQuestion = formatQuestionnaireData(
        userChoice,
        nextQData,
        newQNo,
        newColorIndex,
        filteredOptions
      )
      setQuestionnaire(dataWithNextQuestion)
    }

    if (window.parent && window.parent.dataLayer) {
      window.parent.dataLayer.push({
        event: `datavisualization-wiredHeadphoneQuiz-answerClick-${qNo}`,
        eventLabel: `${selectedOptionData.text}`,
        "gtm.elementLabel": `${selectedOptionData.text}`,
      })
    }
  }

  const EMPTY_LINE = {
    d: "",
    strokeDasharray: 0,
    strokeDashoffset: 0,
  }

  useEffect(() => {
    if (window.innerWidth > BREAKPOINT) {
      // Lines from Headphone to Start button
      if (questionnaire.length === 1 && !startQuiz) {
        if (window.innerWidth > 700) {
          const path0 = getPathFromHeadphoneToStart(
            startRef,
            headphoneRef,
            svgRef
          )
          setPaths([path0])
          // Computing it twice
          // Delay by 1s to let React render the headphone image
          // with proper size in case of slow networks
          setTimeout(() => {
            const path0 = getPathFromHeadphoneToStart(
              startRef,
              headphoneRef,
              svgRef
            )
            setPaths([path0])
          }, 1000)
        } else {
          setPaths([EMPTY_LINE])
        }
      }
      // Lines from Start button to Q1
      else if (questionnaire.length === 1 && startQuiz) {
        const allPaths = getPathsFromStartToQ1(
          questionnaire,
          headerCardRef,
          startRef,
          svgRef
        )
        setPaths((paths) => [...paths, ...allPaths])
      } else if (questionnaire.length > 1) {
        // Connector lines from current Q to next Q
        const allPaths = getPathsBetweenQuestions(questionnaire, svgRef)
        setPaths((paths) => [...paths, ...allPaths])
      }
    }
    if (questionnaire.length > 1) {
      // Scroll to next question, wait for 100ms for better UX
      setTimeout(() => {
        const answeredQuestionRef = questionnaire[questionnaire.length - 1].qRef
        window.scrollTo({
          behavior: "smooth",
          top: getScrollPosition(answeredQuestionRef),
        })
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaire, startQuiz])

  // Recalculate the lines when screensize / orientation changes
  useEffect(() => {
    if (window.innerWidth > BREAKPOINT) {
      let localPaths = []

      if (window.innerWidth > 700) {
        // headphone to start button
        const path0 = getPathFromHeadphoneToStart(
          startRef,
          headphoneRef,
          svgRef
        )
        localPaths = [path0]
      } else {
        localPaths = [EMPTY_LINE]
      }

      if (questionnaire.length > 1) {
        // header to Q1
        const allPaths1 = getPathsFromStartToQ1(
          questionnaire,
          headerCardRef,
          startRef,
          svgRef
        )
        localPaths.push(...allPaths1)

        let recalc = [questionnaire[0]]
        for (let i = 1; i < questionnaire.length; i++) {
          recalc.push(questionnaire[i])
          const allPaths2 = getPathsBetweenQuestions(recalc, svgRef)
          localPaths.push(...allPaths2)
        }
      }
      setPaths(localPaths)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appWidth, appHeight])

  useEffect(() => {
    if (showResults) {
      // Scroll to next question, wait for 100ms for better UX
      setTimeout(() => {
        window.scrollTo({
          behavior: "smooth",
          top: getScrollPosition(resultRef),
        })
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResults])

  useEffect(() => {
    if (resetQuiz) {
      setPaths((paths) => paths.slice(0, 1))
      scrollToHeader()
      const questionNo = 1,
        index = 0
      setQNo(questionNo)
      setColorIndex(index)
      setQuestionnaire(formatQuestionnaireData([], data[0], questionNo, index))
      setPossibleResults(RESULT_IDS)
      setTimeout(() => {
        setShowResults(false)
        setHeadphoneId(null)
        setResetQuiz(false)
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetQuiz])

  const generateQuestions = (state) => {
    return state.map((question, i) => {
      if (question.loading) {
        return (
          <Loading
            key={i}
            ref={question.qRef}
            headingRef={question.headingRef}
          />
        )
      }
      return (
        <Question
          key={i}
          data={question}
          updateChoice={updateChoice}
          colors={question.colors}
        />
      )
    })
  }

  return (
    <section className={classes.container} ref={quizRef}>
      <div className={classes.questions}>
        {generateQuestions(questionnaire)}
      </div>
      {/* <div className={classes.results}>
        {possibleResults ? possibleResults.join(", ") : "No results"}
      </div> */}
    </section>
  )
}

export default withStyles(styles)(Quiz)
